import { useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, unwrap } from "../api.ts";
import { Badge, Button, Card, ErrorText, Field, Input, PageHeader, Spinner, Switch } from "../components/ui.tsx";
import { useBrand } from "../hooks.ts";

type Data = Awaited<ReturnType<typeof load>>;
type ConnectorRow = Data["connectors"][number];
const load = () => unwrap(api.admin.messaging.$get());

const stateTone = { connected: "good", connecting: "accent", pairing: "accent", error: "bad", disabled: "neutral" } as const;
const stateLabel = { connected: "Connected", connecting: "Connecting", pairing: "Waiting for QR scan", error: "Error", disabled: "Off" } as const;

export function MessagingPage() {
  const q = useQuery({
    queryKey: ["messaging"],
    queryFn: load,
    // Poll while something is in flight so the QR code and status stay live.
    refetchInterval: (query) => (query.state.data?.connectors.some((c) => ["connecting", "pairing"].includes(c.status.state)) ? 3000 : 15000),
  });
  if (q.isPending) return <div className="grid place-items-center py-16"><Spinner /></div>;
  if (q.error) return <ErrorText error={q.error} />;
  const by = (p: ConnectorRow["platform"]) => q.data.connectors.find((c) => c.platform === p)!;
  return (
    <>
      <PageHeader
        title="Messaging"
        description="Let people talk to their own assistant from Slack, Discord or WhatsApp. One bot per app; every message is answered by the sender's personal assistant, never anyone else's."
      />
      <div className="space-y-6">
        <SlackCard c={by("slack")} data={q.data} />
        <DiscordCard c={by("discord")} />
        <WhatsAppCard c={by("whatsapp")} />
      </div>
      <p className="mt-6 text-xs text-[var(--muted)]">Who may use each app is set per group: People &amp; groups → Groups → the group → Access.</p>
    </>
  );
}

function Shell({ title, c, children }: { title: string; c: ConnectorRow; children: ReactNode }) {
  return (
    <Card className="p-6">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <h2 className="text-base font-semibold">{title}</h2>
        <Badge tone={stateTone[c.status.state]}>{stateLabel[c.status.state]}</Badge>
        {c.status.detail && c.status.state !== "disabled" && <span className="text-sm text-[var(--muted)]">{c.status.detail}</span>}
      </div>
      {children}
    </Card>
  );
}

function Steps({ items }: { items: ReactNode[] }) {
  return (
    <ol className="mb-5 space-y-2 text-sm">
      {items.map((s, i) => (
        <li key={i} className="flex gap-3">
          <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-[var(--panel-2)] text-xs font-semibold">{i + 1}</span>
          <span className="pt-0.5">{s}</span>
        </li>
      ))}
    </ol>
  );
}

function useSave(platform: ConnectorRow["platform"]) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (json: { enabled: boolean; respondInChannels: boolean; secrets?: Record<string, string> }) =>
      unwrap(api.admin.messaging[":platform"].$put({ param: { platform }, json })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["messaging"] }),
  });
}

const A = ({ href, children }: { href: string; children: ReactNode }) => (
  <a href={href} target="_blank" rel="noreferrer noopener" className="font-medium text-[var(--color-accent)] underline underline-offset-2">{children}</a>
);

function SlackCard({ c, data }: { c: ConnectorRow; data: Data }) {
  const brand = useBrand();
  const save = useSave("slack");
  const [enabled, setEnabled] = useState(c.enabled);
  const [channels, setChannels] = useState(c.respondInChannels);
  const [botToken, setBot] = useState("");
  const [appToken, setApp] = useState("");
  const [copied, setCopied] = useState(false);
  const manifest = JSON.stringify(data.slackManifest);
  const createUrl = `https://api.slack.com/apps?new_app=1&manifest_json=${encodeURIComponent(manifest)}`;
  const secrets = { ...(botToken && { botToken }), ...(appToken && { appToken }) };
  return (
    <Shell title="Slack" c={c}>
      <Steps
        items={[
          <>Open <A href={createUrl}>Create the {brand.name} Slack app</A>, pick your workspace and confirm (the manifest is prefilled). <button className="text-[var(--color-accent)] hover:underline" onClick={() => { void navigator.clipboard.writeText(JSON.stringify(data.slackManifest, null, 2)); setCopied(true); }}>{copied ? "Manifest copied" : "Copy manifest"}</button></>,
          <>Under <b>Install App</b>, install it to the workspace, then copy the <b>Bot User OAuth Token</b> (<code>xoxb-…</code>).</>,
          <>Under <b>Basic Information → App-Level Tokens</b>, generate a token with the <code>connections:write</code> scope (<code>xapp-…</code>).</>,
          <>Paste both below and switch Slack on. People DM <b>@{brand.name}</b>; they're matched to their {brand.name} account by their Slack email automatically.</>,
        ]}
      />
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); save.mutate({ enabled, respondInChannels: channels, ...(Object.keys(secrets).length && { secrets }) }); }}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Bot token" hint={c.storedSecrets.includes("botToken") ? "Stored. Leave blank to keep." : undefined}>
            <Input type="password" autoComplete="off" placeholder="xoxb-…" value={botToken} onChange={(e) => setBot(e.target.value)} className="font-mono" />
          </Field>
          <Field label="App-level token" hint={c.storedSecrets.includes("appToken") ? "Stored. Leave blank to keep." : undefined}>
            <Input type="password" autoComplete="off" placeholder="xapp-…" value={appToken} onChange={(e) => setApp(e.target.value)} className="font-mono" />
          </Field>
        </div>
        <Toggles enabled={enabled} setEnabled={setEnabled} channels={channels} setChannels={setChannels} where="channels" />
        {channels && (
          <p className="rounded-lg bg-[var(--panel-2)] px-3 py-2 text-xs text-[var(--muted)]">
            Once someone @mentions {brand.name} in a channel, it keeps answering replies in that thread without being tagged — each person is answered by their own assistant, and the thread shows up in each of their chats in the web app. Messages that @mention someone else are left alone.
            {" "}An app created before this needs two more bot events: in the Slack app settings, open <b>Event Subscriptions → Subscribe to bot events</b> and add <code>message.channels</code> and <code>message.groups</code>, then reinstall the app.
          </p>
        )}
        <ErrorText error={save.error} />
        <div className="flex justify-end"><Button type="submit" variant="primary" disabled={save.isPending}>Save</Button></div>
      </form>
    </Shell>
  );
}

function DiscordCard({ c }: { c: ConnectorRow }) {
  const brand = useBrand();
  const save = useSave("discord");
  const [enabled, setEnabled] = useState(c.enabled);
  const [channels, setChannels] = useState(c.respondInChannels);
  const [botToken, setBot] = useState("");
  return (
    <Shell title="Discord" c={c}>
      <Steps
        items={[
          <>At <A href="https://discord.com/developers/applications">Discord Developer Portal</A>, create a <b>New Application</b> named {brand.name}.</>,
          <>Open <b>Bot</b>: click <b>Reset Token</b> and copy it, and turn on <b>Message Content Intent</b> (under Privileged Gateway Intents).</>,
          <>Paste the token below and switch Discord on.</>,
          c.status.inviteUrl ? (
            <>Add the bot to your server: <A href={c.status.inviteUrl}>Invite {brand.name}</A>. People DM the bot and send <b>link</b> once to connect their {brand.name} account.</>
          ) : (
            <>Once connected, an invite link for your server appears here. People DM the bot and send <b>link</b> once to connect their {brand.name} account.</>
          ),
        ]}
      />
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); save.mutate({ enabled, respondInChannels: channels, ...(botToken && { secrets: { botToken } }) }); }}>
        <Field label="Bot token" hint={c.storedSecrets.includes("botToken") ? "Stored. Leave blank to keep." : undefined}>
          <Input type="password" autoComplete="off" value={botToken} onChange={(e) => setBot(e.target.value)} className="font-mono" />
        </Field>
        <Toggles enabled={enabled} setEnabled={setEnabled} channels={channels} setChannels={setChannels} where="server channels" />
        <ErrorText error={save.error} />
        <div className="flex justify-end"><Button type="submit" variant="primary" disabled={save.isPending}>Save</Button></div>
      </form>
    </Shell>
  );
}

function WhatsAppCard({ c }: { c: ConnectorRow }) {
  const brand = useBrand();
  const qc = useQueryClient();
  const save = useSave("whatsapp");
  const logout = useMutation({
    mutationFn: () => unwrap(api.admin.messaging.whatsapp.logout.$post()),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["messaging"] }),
  });
  return (
    <Shell title="WhatsApp" c={c}>
      <div className="mb-5 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
        <b>Use a dedicated phone number.</b> This connects through WhatsApp Web, which WhatsApp doesn't officially support for bots; the
        number can be banned at any time without warning. Never use a personal or business-critical number.
      </div>
      <Steps
        items={[
          <>Put WhatsApp on a spare phone with a dedicated number.</>,
          <>Switch WhatsApp on below. A QR code appears here.</>,
          <>On that phone: <b>WhatsApp → Settings → Linked devices → Link a device</b>, and scan the code.</>,
          <>People message that number and send <b>link</b> once to connect their {brand.name} account. Groups are ignored.</>,
        ]}
      />
      {c.status.qr && (
        <div className="mb-5 flex flex-col items-center gap-2">
          <img src={c.status.qr} alt="WhatsApp pairing QR code" className="h-64 w-64 rounded-lg border border-[var(--line)] bg-white p-2" />
          <span className="text-xs text-[var(--muted)]">The code refreshes every ~20 seconds.</span>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="w-72">
          <Switch label="WhatsApp on" checked={c.enabled} disabled={save.isPending} onChange={(v) => save.mutate({ enabled: v, respondInChannels: false })} />
        </div>
        {(c.enabled || c.status.handle) && (
          <Button variant="danger" disabled={logout.isPending} onClick={() => confirm("Unpair this WhatsApp number? You'll need to scan a new QR code to reconnect.") && logout.mutate()}>
            Unpair number
          </Button>
        )}
      </div>
      <ErrorText error={save.error ?? logout.error} />
    </Shell>
  );
}

function Toggles({ enabled, setEnabled, channels, setChannels, where }: { enabled: boolean; setEnabled: (v: boolean) => void; channels: boolean; setChannels: (v: boolean) => void; where: string }) {
  return (
    <div className="grid gap-x-8 sm:grid-cols-2">
      <Switch label="Enabled" hint="Direct messages to the bot" checked={enabled} onChange={setEnabled} />
      <Switch
        label={`Also answer @mentions in ${where}`}
        hint="Replies are visible to everyone in the channel — a personal assistant may reveal the sender's own files or memories there."
        checked={channels}
        onChange={setChannels}
      />
    </div>
  );
}
